// SPDX-License-Identifier: Apache-2.0
import GirafeSingleton from '../../base/GirafeSingleton';
import CustomTheme from '../../models/customtheme';
import ThemeLayer from '../../models/layers/themelayer';

class ThemeFavoritesManager extends GirafeSingleton {
  private themeFavorites: Array<number | string> = [];
  private readonly storagePath: string = 'themeFavorites';

  public override async initializeSingleton() {
    await super.initializeSingleton();
    await this.loadThemeFavorites();
  }

  private async saveThemeFavorites() {
    const serializedThemeFavorites: string = JSON.stringify(this.themeFavorites);
    await this.context.userDataManager.saveUserData(this.storagePath, serializedThemeFavorites);
  }

  private async loadThemeFavorites() {
    const serializedThemeFavorites = (await this.context.userDataManager.getUserData(this.storagePath)) as string;
    if (serializedThemeFavorites) {
      this.themeFavorites = JSON.parse(serializedThemeFavorites);
    }
  }

  public async addThemeToFavorites(theme: ThemeLayer | CustomTheme) {
    this.themeFavorites.push(theme instanceof ThemeLayer ? theme.id : theme.name);
    await this.saveThemeFavorites();
  }

  public async removeThemeFromFavorites(theme: ThemeLayer | CustomTheme) {
    const index = this.themeFavorites.findIndex((themeIdOrName) =>
      theme instanceof ThemeLayer ? themeIdOrName === theme.id : themeIdOrName === theme.name
    );
    if (index >= 0) {
      this.themeFavorites.splice(index, 1);
      await this.saveThemeFavorites();
    } else {
      throw new Error('The theme to be removed cannot be found in the list of favorites');
    }
  }

  public async addOrRemoveThemeFromFavorites(theme: ThemeLayer | CustomTheme) {
    if (this.isThemeInFavorites(theme)) {
      await this.removeThemeFromFavorites(theme);
    } else {
      await this.addThemeToFavorites(theme);
    }
  }

  public async clearThemeFavorites() {
    this.themeFavorites = [];
    await this.saveThemeFavorites();
  }

  public isThemeInFavorites(theme: ThemeLayer | CustomTheme): boolean {
    return this.themeFavorites.includes(theme instanceof ThemeLayer ? theme.id : theme.name);
  }

  public getThemeFavorites(): Array<number | string> {
    return this.themeFavorites;
  }
}

export default ThemeFavoritesManager;
